import { HttpErrorResponse } from '@angular/common/http';
import * as Sentry from '@sentry/angular';

import { environment } from '#environments/environment';
import * as developmentEnvironment from '#environments/environment.development';
import * as productionEnvironment from '#environments/environment.prod';
import * as stagingEnvironment from '#environments/environment.staging';

const errorsToFilter = [
    /ChunkLoadError/,
    /{"headers":{"normalizedNames/,
    /<unknown>/,
    /Non-Error exception captured with keys/,
    /Http failure response for/,
    /Non-Error promise rejection/,
    // TODO: add the esbuild dynamic-import failures here — 'Failed to fetch dynamically imported module', 'Importing a module script failed', 'not a valid JavaScript MIME type' — once the app reloads itself on a chunk error.
    // Until it does, these are the only signal that a user is stuck on a build that no longer exists on the CDN, so filtering them would hide a real and fixable breakage.
];

/**
 * A request the browser never completed surfaces as an HttpErrorResponse with status 0: offline, tab closed mid-flight, connection dropped, request cancelled.
 * It says nothing about the app, so it is dropped here rather than in a Sentry inbound filter, which still bills the event.
 */
const isUncompletedRequest = (error: unknown): boolean => error instanceof HttpErrorResponse && error.status === 0;

/**
 * Sentry's Angular `ErrorHandler` captures an `HttpErrorResponse` as a bare string, its `message`, whenever the response body is not itself an `Error`.
 * A string has no `.message`, so the exception is read by shape: message, name, statusText and url for an HttpErrorResponse, message and name for an Error, the raw text otherwise.
 */
const getFilterableTexts = (error: unknown): string[] => {
    if (error instanceof HttpErrorResponse) {
        return [error.message, error.name, error.statusText, error.url ?? ''];
    }

    if (error instanceof Error) {
        return [error.message, error.name];
    }

    return [typeof error === 'string' ? error : String(error)];
};

const shouldFilterError = (error: unknown): boolean => {
    if (isUncompletedRequest(error)) {
        return true;
    }

    return getFilterableTexts(error)
        .filter(Boolean)
        .some((text) => errorsToFilter.some((pattern) => RegExp(pattern).test(text)));
};

const host = {
    development: developmentEnvironment.environment.APP_MALOU_API_URL,
    staging: stagingEnvironment.environment.APP_MALOU_API_URL,
    prod: productionEnvironment.environment.APP_MALOU_API_URL,
};

export const initSentry = () => {
    if (environment.environment === 'production') {
        Sentry.init({
            release: 'malouapp-v3@latest',
            denyUrls: [/localhost/],
            dsn: 'https://a99c0618253340088a00f7836b92671b@o785966.ingest.sentry.io/4504751295430656',
            maxBreadcrumbs: 30,
            debug: false,
            tracePropagationTargets: ['xhr', 'fetch', host[environment.environment], /^\/api\//],
            integrations: [
                // Registers and configures the Tracing integration,
                // which automatically instruments your application to monitor its
                // performance, including custom Angular routing instrumentation
                Sentry.browserTracingIntegration({
                    traceFetch: true,
                    traceXHR: true,
                }),
                Sentry.replayIntegration({
                    blockAllMedia: false,
                    maskAllInputs: false,
                    maskAllText: false,
                }),
            ],
            ignoreTransactions: [/experimentation/, /ads/],
            ignoreErrors: [
                // Random plugins/extensions
                '<unknown>',
                'top.GLOBALS',
                'originalCreateNotification',
                'canvas.contentDocument',
                'MyApp_RemoveAllHighlights',
                'http://tt.epicplay.com',
                "Can't find variable: ZiteReader",
                'jigsaw is not defined',
                'ComboSearch is not defined',
                'http://loading.retry.widdit.com/',
                'atomicFindClose',
                // Facebook borked
                'fb_xd_fragment',
                'bmi_SafeAddOnload',
                'EBCallBackMessageReceived',
                'conduitPage',
                'Script error',
                // Intern errors Ignored
                'unknown',
            ],
            // Set tracesSampleRate to 1.0 to capture 100%
            // of transactions for performance monitoring.
            // We recommend adjusting this value in production
            tracesSampleRate: 0.1,
            replaysOnErrorSampleRate: 1.0,
            replaysSessionSampleRate: 0.1,
            environment: environment.environment,
            beforeSend(event, hint) {
                if (shouldFilterError(hint.originalException)) {
                    return null;
                }

                // No custom fingerprint on purpose.
                // Appending the message split one bug into an issue per browser, since browsers word the same failure differently.
                return event;
            },
        });
    }
};
